import React, { useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Alert, AlertDescription } from './ui/alert';
import { 
  Palette, 
  RotateCcw, 
  Save, 
  CheckCircle2,
  Monitor,
  Layout,
  Type,
  Paintbrush
} from 'lucide-react';

const ThemeSettings = () => {
  const { theme, updateTheme, resetToDefault, defaultTheme } = useTheme();
  const [localTheme, setLocalTheme] = useState(theme);
  const [success, setSuccess] = useState('');
  const [hasChanges, setHasChanges] = useState(false);

  const handleColorChange = (key, value) => {
    setLocalTheme(prev => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const handleSave = () => {
    updateTheme(localTheme);
    setSuccess('Theme saved successfully! Changes applied across the application.');
    setHasChanges(false);
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleReset = () => {
    if (window.confirm('Reset all theme settings to default clinical blue theme?')) {
      resetToDefault();
      setLocalTheme(defaultTheme);
      setHasChanges(false);
      setSuccess('Theme reset to default.');
      setTimeout(() => setSuccess(''), 3000);
    }
  };

  const colorGroups = [
    {
      title: 'Primary Colors',
      icon: <Palette className="h-5 w-5" />,
      description: 'Main accent colors used throughout the application',
      colors: [
        { key: 'primaryColor', label: 'Primary Color', description: 'Main accent color (buttons, links, highlights)' },
        { key: 'primaryHover', label: 'Primary Hover', description: 'Hover state for primary elements' },
        { key: 'primaryLight', label: 'Primary Light', description: 'Light variant for backgrounds and badges' },
      ]
    },
    {
      title: 'Backgrounds',
      icon: <Monitor className="h-5 w-5" />,
      description: 'Page and content background colors',
      colors: [
        { key: 'backgroundColor', label: 'App Background', description: 'Main application background' },
        { key: 'surfaceColor', label: 'Surface/Card Background', description: 'Cards, panels, and elevated surfaces' },
      ]
    },
    {
      title: 'Navigation',
      icon: <Layout className="h-5 w-5" />,
      description: 'Header and sidebar colors',
      colors: [
        { key: 'headerBackground', label: 'Header Background', description: 'Top navigation bar (Doctor view)' },
        { key: 'menuBackground', label: 'Menu Background', description: 'Side menu background (Doctor view)' },
        { key: 'adminHeaderBackground', label: 'Admin Header', description: 'Admin console header' },
        { key: 'adminMenuBackground', label: 'Admin Menu', description: 'Admin console sidebar' },
      ]
    },
    {
      title: 'Text Colors',
      icon: <Type className="h-5 w-5" />,
      description: 'Typography and text colors',
      colors: [
        { key: 'textPrimary', label: 'Primary Text', description: 'Headings and main content' },
        { key: 'textSecondary', label: 'Secondary Text', description: 'Subtitles and muted text' },
        { key: 'textOnPrimary', label: 'Text on Primary', description: 'Text displayed on primary color backgrounds' },
      ]
    },
    {
      title: 'Borders & Focus',
      icon: <Paintbrush className="h-5 w-5" />,
      description: 'Borders and focus states',
      colors: [
        { key: 'borderColor', label: 'Border Color', description: 'Default border color for inputs and cards' },
        { key: 'focusRingColor', label: 'Focus Ring', description: 'Focus outline color for accessibility' },
      ]
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Theme Settings</h1>
          <p className="text-gray-600 mt-1">
            Customize the application's visual appearance. Changes apply globally.
          </p>
        </div>
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={handleReset}
            className="flex items-center gap-2"
          >
            <RotateCcw className="h-4 w-4" />
            Reset to Default
          </Button>
          <Button
            onClick={handleSave}
            disabled={!hasChanges}
            className="flex items-center gap-2 bg-[var(--theme-primary)] hover:bg-[var(--theme-primary-hover)] text-white"
          >
            <Save className="h-4 w-4" />
            Save Theme
          </Button>
        </div>
      </div>

      {/* Success Message */}
      {success && (
        <Alert className="border-green-200 bg-green-50">
          <AlertDescription className="flex items-center gap-2 text-green-800">
            <CheckCircle2 className="h-4 w-4" />
            {success}
          </AlertDescription>
        </Alert>
      )}

      {/* Live Preview */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Monitor className="h-5 w-5" />
            Live Preview
          </CardTitle>
          <CardDescription>
            See how your theme looks in real-time
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div 
            className="rounded-lg border overflow-hidden"
            style={{ backgroundColor: localTheme.backgroundColor }}
          >
            {/* Preview Header */}
            <div 
              className="p-4 flex items-center justify-between"
              style={{ backgroundColor: localTheme.headerBackground }}
            >
              <span className="font-semibold" style={{ color: localTheme.textOnPrimary }}>
                Exlva DARe
              </span>
              <div className="flex gap-2">
                <div 
                  className="px-3 py-1 rounded text-sm"
                  style={{ 
                    backgroundColor: localTheme.primaryLight,
                    color: localTheme.primaryColor
                  }}
                >
                  Dashboard
                </div>
                <div 
                  className="px-3 py-1 rounded text-sm"
                  style={{ color: localTheme.textOnPrimary }}
                >
                  Settings
                </div>
              </div>
            </div>
            
            {/* Preview Content */}
            <div className="p-6" style={{ backgroundColor: localTheme.surfaceColor }}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Sample Card */}
                <div 
                  className="p-4 rounded-lg border"
                  style={{ 
                    backgroundColor: localTheme.backgroundColor,
                    borderColor: localTheme.borderColor 
                  }}
                >
                  <h3 
                    className="font-semibold mb-2"
                    style={{ color: localTheme.textPrimary }}
                  >
                    Sample Card
                  </h3>
                  <p 
                    className="text-sm mb-3"
                    style={{ color: localTheme.textSecondary }}
                  >
                    This is how cards and content will appear with your theme.
                  </p>
                  <button
                    className="px-4 py-2 rounded-md text-sm font-medium transition-colors"
                    style={{ 
                      backgroundColor: localTheme.primaryColor,
                      color: localTheme.textOnPrimary 
                    }}
                  >
                    Primary Button
                  </button>
                </div>
                
                {/* Sample Form */}
                <div 
                  className="p-4 rounded-lg border"
                  style={{ 
                    backgroundColor: localTheme.backgroundColor,
                    borderColor: localTheme.borderColor 
                  }}
                >
                  <h3 
                    className="font-semibold mb-2"
                    style={{ color: localTheme.textPrimary }}
                  >
                    Form Elements
                  </h3>
                  <div className="space-y-2">
                    <input
                      type="text"
                      placeholder="Sample input"
                      className="w-full px-3 py-2 rounded-md text-sm"
                      style={{ 
                        borderColor: localTheme.borderColor,
                        borderWidth: '1px',
                        borderStyle: 'solid',
                        color: localTheme.textPrimary
                      }}
                    />
                    <div className="flex gap-2">
                      <span 
                        className="px-2 py-1 rounded text-xs"
                        style={{ 
                          backgroundColor: localTheme.primaryLight,
                          color: localTheme.primaryColor
                        }}
                      >
                        Badge
                      </span>
                      <span 
                        className="px-2 py-1 rounded text-xs bg-green-100 text-green-800"
                      >
                        Success
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Color Settings */}
      <div className="grid gap-6">
        {colorGroups.map((group) => (
          <Card key={group.title}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {group.icon}
                {group.title}
              </CardTitle>
              <CardDescription>{group.description}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {group.colors.map((color) => (
                  <div key={color.key} className="space-y-2">
                    <Label htmlFor={color.key} className="text-sm font-medium">
                      {color.label}
                    </Label>
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-10 h-10 rounded-lg border-2 border-gray-200 cursor-pointer overflow-hidden flex-shrink-0"
                        style={{ backgroundColor: localTheme[color.key] }}
                      >
                        <input
                          type="color"
                          id={color.key}
                          value={localTheme[color.key]}
                          onChange={(e) => handleColorChange(color.key, e.target.value)}
                          className="w-full h-full cursor-pointer opacity-0"
                        />
                      </div>
                      <Input
                        type="text"
                        value={localTheme[color.key]}
                        onChange={(e) => handleColorChange(color.key, e.target.value)}
                        className="font-mono text-sm uppercase"
                        placeholder="#000000"
                      />
                    </div>
                    <p className="text-xs text-gray-500">{color.description}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Quick Presets */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Palette className="h-5 w-5" />
            Quick Presets
          </CardTitle>
          <CardDescription>
            Choose from pre-designed color schemes
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {/* Clinical Blue (Default) */}
            <button
              onClick={() => {
                setLocalTheme(defaultTheme);
                setHasChanges(true);
              }}
              className="p-4 rounded-lg border-2 border-gray-200 hover:border-sky-400 transition-colors text-left"
            >
              <div className="flex gap-1 mb-2">
                <div className="w-6 h-6 rounded" style={{ backgroundColor: '#0066CC' }}></div>
                <div className="w-6 h-6 rounded" style={{ backgroundColor: '#FFFFFF' }}></div>
                <div className="w-6 h-6 rounded" style={{ backgroundColor: '#F8FAFC' }}></div>
              </div>
              <p className="text-sm font-medium">Clinical Blue</p>
              <p className="text-xs text-gray-500">Default theme</p>
            </button>

            {/* Teal Medical */}
            <button
              onClick={() => {
                setLocalTheme({
                  ...defaultTheme,
                  primaryColor: '#0D9488',
                  primaryHover: '#0F766E',
                  primaryLight: '#CCFBF1',
                  headerBackground: '#0D9488',
                  focusRingColor: '#0D9488',
                });
                setHasChanges(true);
              }}
              className="p-4 rounded-lg border-2 border-gray-200 hover:border-teal-400 transition-colors text-left"
            >
              <div className="flex gap-1 mb-2">
                <div className="w-6 h-6 rounded" style={{ backgroundColor: '#0D9488' }}></div>
                <div className="w-6 h-6 rounded" style={{ backgroundColor: '#FFFFFF' }}></div>
                <div className="w-6 h-6 rounded" style={{ backgroundColor: '#CCFBF1' }}></div>
              </div>
              <p className="text-sm font-medium">Teal Medical</p>
              <p className="text-xs text-gray-500">Calming healthcare</p>
            </button>

            {/* Navy Professional */}
            <button
              onClick={() => {
                setLocalTheme({
                  ...defaultTheme,
                  primaryColor: '#1E3A8A',
                  primaryHover: '#1E40AF',
                  primaryLight: '#DBEAFE',
                  headerBackground: '#1E3A8A',
                  focusRingColor: '#1E3A8A',
                });
                setHasChanges(true);
              }}
              className="p-4 rounded-lg border-2 border-gray-200 hover:border-sky-800 transition-colors text-left"
            >
              <div className="flex gap-1 mb-2">
                <div className="w-6 h-6 rounded" style={{ backgroundColor: '#1E3A8A' }}></div>
                <div className="w-6 h-6 rounded" style={{ backgroundColor: '#FFFFFF' }}></div>
                <div className="w-6 h-6 rounded" style={{ backgroundColor: '#DBEAFE' }}></div>
              </div>
              <p className="text-sm font-medium">Navy Professional</p>
              <p className="text-xs text-gray-500">Corporate look</p>
            </button>

            {/* Forest Green */}
            <button
              onClick={() => {
                setLocalTheme({
                  ...defaultTheme,
                  primaryColor: '#166534',
                  primaryHover: '#15803D',
                  primaryLight: '#DCFCE7',
                  headerBackground: '#166534',
                  focusRingColor: '#166534',
                });
                setHasChanges(true);
              }}
              className="p-4 rounded-lg border-2 border-gray-200 hover:border-green-600 transition-colors text-left"
            >
              <div className="flex gap-1 mb-2">
                <div className="w-6 h-6 rounded" style={{ backgroundColor: '#166534' }}></div>
                <div className="w-6 h-6 rounded" style={{ backgroundColor: '#FFFFFF' }}></div>
                <div className="w-6 h-6 rounded" style={{ backgroundColor: '#DCFCE7' }}></div>
              </div>
              <p className="text-sm font-medium">Forest Green</p>
              <p className="text-xs text-gray-500">Natural & organic</p>
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ThemeSettings;
